import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Building, Calculator, ClipboardList, Wallet, ArrowLeft, Plus, Download, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = !ENV_BACKEND_URL || ENV_BACKEND_URL === '' ? window.location.origin : ENV_BACKEND_URL;
if (BACKEND_URL.endsWith('/api')) BACKEND_URL = BACKEND_URL.slice(0, -4);
const API = `${BACKEND_URL}/api`;

function formatCLP(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

export default function ProjectDetailsPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjectDetail();
  }, [id]);

  const fetchProjectDetail = async () => {
    try {
      const [projRes, ccRes] = await Promise.all([
        axios.get(`${API}/construction/projects/${id}`),
        axios.get(`${API}/construction/projects/${id}/cost-centers`)
      ]);
      setProject(projRes.data);
      setCostCenters(ccRes.data);
    } catch (error) {
      toast.error('Error al cargar detalle de obra');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-8 text-center font-medium animate-pulse">Cargando detalles de obra...</div>;
  if (!project) return <div className="p-8 text-center text-red-500 font-bold underline decoration-2">Obra no encontrada</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-2">
          <Button variant="ghost" size="icon" asChild>
              <Link to="/projects"><ArrowLeft className="h-6 w-6" /></Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{project.name}</h1>
            <p className="text-muted-foreground font-mono">{project.code} | {project.address}</p>
          </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="md:col-span-2">
            <CardHeader className="border-b pb-4">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Información General</CardTitle>
                <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-950/40 text-indigo-600 rounded-full text-xs font-bold uppercase tracking-wider">
                  En ejecución
                </span>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-8 pt-6">
                <div>
                    <span className="text-sm text-muted-foreground block mb-1">Presupuesto Referencial</span>
                    <span className="text-2xl font-bold font-mono">{formatCLP(project.total_budget || 0)}</span>
                </div>
                <div>
                    <span className="text-sm text-muted-foreground block mb-1">Costo Real Acumulado</span>
                    <span className="text-2xl font-bold font-mono text-red-600">{formatCLP(0)}</span>
                </div>
                <div>
                    <span className="text-sm text-muted-foreground block mb-1">Fecha de Inicio</span>
                    <span className="font-semibold">{project.start_date || 'No definida'}</span>
                </div>
                <div>
                    <span className="text-sm text-muted-foreground block mb-1">Fecha Estimada Término</span>
                    <span className="font-semibold">{project.end_date || 'No definida'}</span>
                </div>
            </CardContent>
          </Card>

          <Card className="bg-indigo-600 text-white border-none shadow-indigo-200 dark:shadow-none">
            <CardHeader>
                <CardTitle className="text-lg opacity-90">Progreso de Obra</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center pt-2">
                <div className="relative h-32 w-32 flex items-center justify-center mb-4">
                    <svg className="h-full w-full transform -rotate-90">
                        <circle cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-white/20" />
                        <circle cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray={364.4} strokeDashoffset={364.4 * (1 - 0.35)} className="text-white" strokeLinecap="round" />
                    </svg>
                    <span className="absolute text-2xl font-bold font-mono">35%</span>
                </div>
                <p className="text-sm opacity-80 font-medium">Avance financiero actual</p>
            </CardContent>
          </Card>
      </div>

      <Tabs defaultValue="budget" className="w-full">
        <TabsList className="w-full justify-start border-b rounded-none h-12 bg-transparent gap-6 mb-4">
          <TabsTrigger value="budget" className="data-[state=active]:border-indigo-600 data-[state=active]:border-b-2 rounded-none bg-transparent px-2 h-full flex gap-2">
            <Calculator className="h-4 w-4" /> Presupuesto/APU
          </TabsTrigger>
          <TabsTrigger value="cost_centers" className="data-[state=active]:border-indigo-600 data-[state=active]:border-b-2 rounded-none bg-transparent px-2 h-full flex gap-2">
            <Building className="h-4 w-4" /> Centros de Costo
          </TabsTrigger>
          <TabsTrigger value="eepp" className="data-[state=active]:border-indigo-600 data-[state=active]:border-b-2 rounded-none bg-transparent px-2 h-full flex gap-2">
            <Wallet className="h-4 w-4" /> Estados de Pago
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:border-indigo-600 data-[state=active]:border-b-2 rounded-none bg-transparent px-2 h-full flex gap-2">
            <ClipboardList className="h-4 w-4" /> Historial / Bitácora
          </TabsTrigger>
        </TabsList>

        <TabsContent value="budget" className="mt-0">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                   <CardTitle className="text-lg">Partidas y Presupuesto (APU)</CardTitle>
                   <p className="text-sm text-muted-foreground">Desglose detallado de recursos por partida</p>
                </div>
                <Button size="sm"><Plus className="h-4 w-4 mr-2" /> Agregar Partida</Button>
            </CardHeader>
            <CardContent>
                <div className="border rounded-xl overflow-hidden">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-muted/50 border-b font-semibold text-muted-foreground">
                            <tr>
                                <th className="px-4 py-3">Código</th>
                                <th className="px-4 py-3">Descripción</th>
                                <th className="px-4 py-3 text-center">Unidad</th>
                                <th className="px-4 py-3 text-right">Cantidad</th>
                                <th className="px-4 py-3 text-right">P.U.</th>
                                <th className="px-4 py-3 text-right">Total</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            <tr className="bg-muted/10 font-bold border-l-4 border-indigo-500">
                                <td className="px-4 py-3">01.01</td>
                                <td className="px-4 py-3" colSpan={4}>MOVIMIENTO DE TIERRAS</td>
                                <td className="px-4 py-3 text-right font-mono">{formatCLP(12500000)}</td>
                                <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 ml-auto opacity-40" /></td>
                            </tr>
                            <tr className="hover:bg-muted/5 group">
                                <td className="px-4 py-3 text-muted-foreground pl-8">01.01.01</td>
                                <td className="px-4 py-3">Excavación Masiva</td>
                                <td className="px-4 py-3 text-center">m3</td>
                                <td className="px-4 py-3 text-right font-mono">450.0</td>
                                <td className="px-4 py-3 text-right font-mono">{formatCLP(15000)}</td>
                                <td className="px-4 py-3 text-right font-mono">{formatCLP(6750000)}</td>
                                <td className="px-4 py-3"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cost_centers" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {costCenters.map(cc => (
                    <Card key={cc.id} className="hover:border-indigo-400 cursor-pointer group transition-all">
                        <CardContent className="p-5">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="font-bold text-lg">{cc.name}</h4>
                                    <p className="text-xs text-muted-foreground font-mono">{cc.code}</p>
                                </div>
                                <Building className="h-5 w-5 text-indigo-500" />
                            </div>
                            <div className="flex justify-between items-end border-t pt-4">
                                <div>
                                    <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-tighter">Ppto. Asignado</span>
                                    <p className="font-bold font-mono text-lg">{formatCLP(cc.budget)}</p>
                                </div>
                                <Button variant="ghost" size="sm" className="group-hover:text-indigo-600 transition-colors">Detalles <ArrowLeft className="h-4 w-4 ml-1 rotate-180" /></Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
                <Card className="border-dashed flex items-center justify-center h-full min-h-[140px] hover:bg-muted/20 cursor-pointer transition-colors">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground group">
                        <Plus className="h-8 w-8 group-hover:scale-110 transition-transform" />
                        <span className="font-medium">Nuevo Centro de Costo</span>
                    </div>
                </Card>
            </div>
        </TabsContent>

        <TabsContent value="eepp" className="mt-0">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                   <CardTitle className="text-lg">Estados de Pago (EEPP)</CardTitle>
                   <p className="text-sm text-muted-foreground">Historial de valorización de obra y retenciones</p>
                </div>
                <Button size="sm" variant="outline"><Download className="h-4 w-4 mr-2" /> Exportar Planilla</Button>
            </CardHeader>
            <CardContent>
                <div className="p-8 text-center border-2 border-dashed rounded-xl">
                    <Wallet className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
                    <p className="text-muted-foreground font-medium mb-4">No se han generado estados de pago para esta obra.</p>
                    <Button>Generar Primer Estado de Pago</Button>
                </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
